import { CliplisterProvider, CliplisterViewer, CliplisterViewerQuality } from './cliplister-provider';
import { CliplisterStage } from './cliplister-settings';

/* reflects current state of player in events */
export type VideoPlayerEvent = {
    videoCount: number;
    videoLength: number;
    videoPosition: number;
    videoQuality: CliplisterViewerQuality;
    isFullscreen: boolean;
};

export type VideoPlayerConfig = {
    parentId: string;
    assetKey: string;
    backgroundColor: string;
    stage: CliplisterStage;
    autoplay: boolean;
    withNextButton: boolean;
    onFullscreenChange?: (event: VideoPlayerEvent) => void;
    onProgress10?: (event: VideoPlayerEvent) => void;
    onProgress50?: (event: VideoPlayerEvent) => void;
    onProgress90?: (event: VideoPlayerEvent) => void;
    onQualityChange?: (event: VideoPlayerEvent) => void;
    // called when asset's first video is started for the first time
    onInitialVideoStart?: (event: VideoPlayerEvent) => void;
    // called when player changes to next video of asset
    onChangeVideo?: (event: VideoPlayerEvent) => void;
    onPause?: (playerState: VideoPlayerEvent) => void;
    onPlayAfterPause?: (playerState: VideoPlayerEvent) => void;
    onAspectRatioChange?: (aspectRatio: string) => void;
};

export class VideoPlayer {
    private config: VideoPlayerConfig;
    private cliplisterViewer?: CliplisterViewer;

    constructor(config: VideoPlayerConfig) {
        this.config = config;
    }

    public async init(): Promise<void> {
        if (this.cliplisterViewer) {
            return;
        }

        const currentState: VideoPlayerEvent = {
            videoCount: 0,
            videoLength: 0,
            videoPosition: 0,
            videoQuality: 'S',
            isFullscreen: false,
        };

        let firstOnReadyEvent = true;
        let veryFirstOnPlayEvent = true;
        let firstQualityChangeEvent = true;
        // whether the video playback was started at least once, after being ready
        let hadAnyPlayback = false;
        let currentAspectRatio = '';

        const cliplisterViewer = await CliplisterProvider.getNewViewerInstance({
            parentId: this.config.parentId,
            assetKey: this.config.assetKey,
            backgroundColor: this.config.backgroundColor,
            stage: this.config.stage,
            withNextButton: this.config.withNextButton,
        });

        cliplisterViewer.onFullscreenChange(() => {
            currentState.isFullscreen = cliplisterViewer.isFullscreen();
            this.config.onFullscreenChange?.(currentState);
        });

        cliplisterViewer.onProgress10(() => {
            this.config.onProgress10?.(currentState);
        });

        cliplisterViewer.onProgress50(() => {
            this.config.onProgress50?.(currentState);
        });

        cliplisterViewer.onProgress90(() => {
            this.config.onProgress90?.(currentState);
        });

        cliplisterViewer.onQualityChange(() => {
            // Initiales QualityChange-Event ignorieren, da der Player dieses Event auch direkt nach dem Start schickt.
            // Uns interessiert das Event aber nur, wenn der User bewusst die Qualität über die GUI ändert.
            if (firstQualityChangeEvent) {
                firstQualityChangeEvent = false;
            } else {
                currentState.videoQuality = cliplisterViewer.getCurrentQuality().display;
                this.config.onQualityChange?.(currentState);
            }
        });

        cliplisterViewer.onPlay(() => {
            // - emit 'play' for initial playback of asset's first video
            // - emit 'play' for playback after pause
            // - emit 'changevideo' for initial playback of subsequent video(s)
            if (veryFirstOnPlayEvent) {
                veryFirstOnPlayEvent = false;
                this.config.onInitialVideoStart?.(currentState);
                hadAnyPlayback = true;
            } else if (hadAnyPlayback) {
                this.config.onPlayAfterPause?.(currentState);
            } else {
                this.config.onChangeVideo?.(currentState);
                hadAnyPlayback = true;
            }
        });

        cliplisterViewer.onPause(() => this.config.onPause?.(currentState));

        cliplisterViewer.onReady(() => {
            const assets = cliplisterViewer.getAssets();
            const currentAssetIndex = cliplisterViewer.getCurrentAssetIndex();
            const currentAsset = assets[currentAssetIndex];

            const aspectRatio = assets[currentAssetIndex].aspectratio;
            if (aspectRatio !== currentAspectRatio) {
                currentAspectRatio = aspectRatio;
                this.config.onAspectRatioChange?.(aspectRatio);
            }

            currentState.videoCount = assets.length;
            currentState.videoLength = currentAsset.duration;
            currentState.videoPosition = currentAssetIndex + 1;
            currentState.videoQuality = cliplisterViewer.getCurrentQuality().display;

            hadAnyPlayback = false;
            firstQualityChangeEvent = true;

            if (firstOnReadyEvent && this.config.autoplay) {
                cliplisterViewer.play();
            }
            firstOnReadyEvent = false;
        });

        this.cliplisterViewer = cliplisterViewer;
    }

    public play() {
        this.cliplisterViewer?.play();
    }

    public pause() {
        this.cliplisterViewer?.pause();
    }

    public destroy(): void {
        if (this.cliplisterViewer) {
            this.cliplisterViewer.destroy();
            this.cliplisterViewer = undefined;
        }
    }
}
